import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, X, User, AlertTriangle, Plus, Trash2, Ruler, ShoppingBag, Pencil, ChevronDown, DollarSign, Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn, parseDataSemFuso, formatarDataParaBanco, normalizarTelefone, formatarTelefoneInput, normalizarBusca, podeEditarFicha, rotuloBotaoFicha } from "@/lib/utils";
import { papelDaRefoto, diferencasDaRefoto } from "@/lib/refoto";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTravaSubmit } from "@/hooks/useTravaSubmit";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useProvasFicha, useAdicionarProva, useDeletarProva } from "@/hooks/useProvasFicha";
import { usePedidosFicha, type Pedido } from "@/hooks/usePedidosFicha";
import { useVendedoresUnidade } from "@/hooks/useVendedoresUnidade";
import { useTagsAtivas } from "@/hooks/useTagsAtivas";
import { useLanificios } from "@/hooks/useLanificios";
import { useOpcoesFicha } from "@/hooks/useOpcoesFicha";
import { useTiposItemAvulso } from "@/hooks/useTiposItemAvulso";
import PedidoModal from "@/components/PedidoModal";

const CATEGORIAS_ALUGUEL = ['Básico', 'Intermediário', 'Premium'] as const;

// Quais campos preenchidos abrem cada seção do accordion de "Detalhes do Item".
const seccoesComDados = (f: {
  paleto?: string | null;
  calca?: string | null;
  paleto_cor?: string | null;
  lanificio_id?: string | null;
  paleto_lanificio?: string | null;
  paleto_categoria?: string | null;
  camisa?: string | null;
  camisa_fios?: string | null;
  camisa_cor?: string | null;
  sapato?: string | null;
  sapato_tipo?: string | null;
}): string[] => {
  const secoes: string[] = [];
  if (f.paleto || f.calca || f.paleto_cor || f.lanificio_id || f.paleto_lanificio || f.paleto_categoria) {
    secoes.push('paleto-calca');
  }
  if (f.camisa || f.camisa_fios || f.camisa_cor) secoes.push('camisa');
  if (f.sapato || f.sapato_tipo) secoes.push('sapato');
  return secoes;
};



export default function EditarFichaV3() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, activeUnidade } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = activeUnidade?.role === 'administrativo';
  const travarSubmit = useTravaSubmit();
  const { imageFile, isNewFicha, cliente_id, duplicateAlert, duplicateCodigo, duplicatePapel, isManual } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [isLoadingFicha, setIsLoadingFicha] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState<string | null>(null);
  const [signedImageUrl, setSignedImageUrl] = useState<string | null>(null);
  const [ficha, setFicha] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [wasProcessed, setWasProcessed] = useState(false);
  const [showDuplicateBanner, setShowDuplicateBanner] = useState<boolean>(!!duplicateAlert);
  const [fichaVendedorId, setFichaVendedorId] = useState<string | undefined>(undefined);
  const [pedidoModalOpen, setPedidoModalOpen] = useState(false);
  const [pedidoEditando, setPedidoEditando] = useState<Pedido | undefined>();
  const [buscaTag, setBuscaTag] = useState("");

  const { data: pedidos = [] } = usePedidosFicha(id);
  const { data: provas = [] } = useProvasFicha(id);

  const { data: vendedores = [] } = useVendedoresUnidade();
  const { data: lanificios = [] } = useLanificios();
  const { data: opcoes } = useOpcoesFicha();
  const { data: tiposItem = [] } = useTiposItemAvulso();
  const adicionarProva = useAdicionarProva(id);
  const deletarProva = useDeletarProva(id);

  // Seções abertas do accordion "Detalhes do Item" (controlado).
  const [openSections, setOpenSections] = useState<string[]>([]);
  // Ref do bloco de lanifício/categoria — usado para scrollIntoView pelo botão dinâmico.
  const blocoLanificioRef = useRef<HTMLDivElement>(null);

  // Ficha já lançada = fora de pendente/erro. Após o 1º lançamento a ficha entra
  // em "modo ações": qualquer vendedor da unidade pode lançar prova / pedido avulso,
  // marcar como pago e adicionar tags, mas os campos-núcleo (cadastro/peças/valores)
  // ficam travados nesta tela — alterações de cadastro passam pela tela do cliente.
  // A venda e a comissão continuam do vendedor original (vendedor_id não é reatribuído).
  const jaLancada = !!ficha && !['pendente', 'erro'].includes(ficha.status);
  const nucleoBloqueado = jaLancada;

  // Telefone só pode ser definido/corrigido enquanto a ficha ainda não foi finalizada
  // (status pendente/erro). Após o primeiro salvamento (status vira ativa) ele é
  // congelado — o trigger trg_fichas_bloqueia_troca_telefone garante isso no banco.
  const telefoneBloqueado = jaLancada;

  // Ownership: um 'vendedor' só edita o núcleo da própria ficha. Ao abrir a ficha
  // de outro vendedor, a tela fica em modo somente-leitura do núcleo — mas ele ainda
  // pode lançar provas/pedidos avulsos (marcados em seu nome) e marcar como pago.
  // Gestor e master editam qualquer ficha. O administrativo edita enquanto a ficha
  // não foi lançada — é o que permite a ele atribuir a venda a outro vendedor no
  // seletor abaixo, já que nesse caso fichaVendedorId deixa de ser o id dele.
  // Espelha o USING da RLS fichas_update (migration 20260921120000).
  const soLeituraDono = !podeEditarFicha(activeUnidade?.role, user?.id, fichaVendedorId, jaLancada);

  // Núcleo (cadastro/peças/valores) fica read-only por status OU por não ser o dono.
  const nucleoReadOnly = nucleoBloqueado || soLeituraDono;

  // Ficha lançada trava o accordion de "Detalhes do Item" (dentro do fieldset).
  // Abre as seções para que os valores continuem visíveis mesmo sem poder editar.
  useEffect(() => {
    if (nucleoReadOnly) setOpenSections(['paleto-calca', 'camisa', 'sapato']);
  }, [nucleoReadOnly]);

  const [formData, setFormData] = useState({
    nome_cliente: "",
    telefone_cliente: "",
    codigo_ficha: "",
    tipo: "aluguel",
    status: "pendente",
    data_retirada: undefined as Date | undefined,
    data_devolucao: undefined as Date | undefined,
    data_festa: undefined as Date | undefined,
    valor: "",
    garantia: "",
    paleto: "",
    calca: "",
    camisa: "",
    sapato: "",
    paleto_cor: null as string | null,
    paleto_lanificio: null as string | null,
    lanificio_id: null as string | null,
    sob_medida: false,
    paleto_categoria: null as string | null,
    camisa_fios: null as string | null,
    camisa_cor: null as string | null,
    sapato_tipo: null as string | null,
    pago: false,
    // Tags do cliente por id — a criação livre acabou (RLS de tags só permite
    // escrita à gestão); o vendedor escolhe entre as existentes.
    tags: [] as { id: string; nome: string }[],
  });

  const { data: tagsAtivas = [] } = useTagsAtivas();
  const tagsPadrao = useMemo(() => tagsAtivas.filter(t => t.padrao), [tagsAtivas]);

  useEffect(() => {
    const loadFicha = async () => {
      if (!id) {
        navigate("/fichas");
        return;
      }

      setIsLoadingFicha(true);
      try {
        const { data: fichaData, error } = await supabase
          .from('fichas')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        if (!fichaData) {
          navigate("/fichas");
          return;
        }

        /**
         * Duplicata de código: a ficha nova NÃO é mais apagada.
         *
         * O delete levava junto o `oportunidade_id` dela, e o card do funil
         * ficava aberto para sempre. Sem rastro, ainda: o delete não passa por
         * `excluir_ficha` e a FK de `fichas_ocr_log` é ON DELETE CASCADE, então
         * até o log do OCR ia embora. Agora a edge deixa a ficha em 'inativa',
         * que não aparece em lista nenhuma, não conta faturamento e não dispara
         * o gatilho de auto-ganha, mas guarda o vínculo (IGO-182).
         *
         * O status ficou FORA da condição de propósito: `erro_etapa` é o que
         * identifica o caso, e assim as linhas antigas, que ficaram em 'erro'
         * antes desta mudança, continuam sendo tratadas do mesmo jeito.
         */
        if (
          fichaData.erro_etapa === 'ficha_duplicada' &&
          fichaData.ficha_original_id &&
          isNewFicha
        ) {
          navigate(`/editar-ficha-v3/${fichaData.ficha_original_id}`, {
            state: {
              isNewFicha: false,
              duplicateAlert: true,
              duplicateCodigo: fichaData.codigo_ficha,
              // O que o OCR leu no papel novo. A linha inativa guarda isso
              // desde 01/09, e é o que permite dizer ao vendedor O QUE mudou
              // em vez de só "esta ficha já existe" (IGO-182).
              duplicatePapel: papelDaRefoto(fichaData),
            },
            replace: true,
          });
          return;
        }

        setFicha(fichaData);
        setFichaVendedorId(fichaData.vendedor_id ?? user?.id);

        if (!fichaData.codigo_ficha && fichaData.status === 'pendente' && !isManual) {
          setIsProcessing(true);
        }

        let clienteTags: { id: string; nome: string }[] = [];
        if (fichaData.cliente_id) {
          try {
            const { data: relacoes, error } = await supabase
              .from('relacao_cliente_tag')
              .select('id_tag, tags(id, nome)')
              .eq('id_cliente', fichaData.cliente_id);

            if (!error && relacoes) {
              clienteTags = relacoes
                .map(r => (r as any).tags)
                .filter((t): t is { id: string; nome: string } => !!t?.id);
            }
          } catch (error) {
            console.error('Erro ao buscar tags:', error);
          }
        }

        setFormData(prev => ({
          ...prev,
          nome_cliente: fichaData.nome_cliente || "",
          telefone_cliente: formatarTelefoneInput(fichaData.telefone_cliente),
          codigo_ficha: fichaData.codigo_ficha || "",
          tipo: fichaData.tipo || "aluguel",
          status: fichaData.status || "pendente",
          data_retirada: parseDataSemFuso(fichaData.data_retirada),
          data_devolucao: parseDataSemFuso(fichaData.data_devolucao),
          data_festa: parseDataSemFuso(fichaData.data_festa),
          valor: fichaData.valor?.toString() || "",
          garantia: fichaData.garantia?.toString() || "",
          paleto: fichaData.paleto || "",
          calca: fichaData.calca || "",
          camisa: fichaData.camisa || "",
          sapato: fichaData.sapato || "",
          paleto_cor: fichaData.paleto_cor || null,
          paleto_lanificio: fichaData.paleto_lanificio || null,
          lanificio_id: fichaData.lanificio_id || null,
          sob_medida: fichaData.sob_medida || false,
          paleto_categoria: fichaData.paleto_categoria || null,
          camisa_fios: fichaData.camisa_fios || null,
          camisa_cor: fichaData.camisa_cor || null,
          sapato_tipo: fichaData.sapato_tipo || null,
          pago: fichaData.pago || false,
          tags: clienteTags,
        }));

        // Abre as seções que já têm dado; ficha manual nova (tudo vazio) = tudo fechado.
        setOpenSections(seccoesComDados(fichaData));
      } catch (error) {
        console.error('Erro ao carregar ficha:', error);
        navigate("/fichas");
      } finally {
        setIsLoadingFicha(false);
      }
    };

    loadFicha();
  }, [id, navigate, isNewFicha, isManual]);

  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel(`ficha-v3-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'fichas',
          filter: `id=eq.${id}`
        },
        (payload) => {
          const fichaAtualizada = payload.new;
          const codigoAnterior = ficha?.codigo_ficha;
          const codigoNovo = fichaAtualizada.codigo_ficha;

          if (!codigoAnterior && codigoNovo) {
            setIsProcessing(false);
            setWasProcessed(true);
            setTimeout(() => setWasProcessed(false), 5000);
          }

          if (
            fichaAtualizada.erro_etapa === 'ficha_duplicada' &&
            fichaAtualizada.ficha_original_id &&
            isNewFicha
          ) {
            setIsProcessing(false);
            // Não apaga mais. Mesmo motivo do bloco de carregamento acima.
            navigate(`/editar-ficha-v3/${fichaAtualizada.ficha_original_id}`, {
              state: {
                isNewFicha: false,
                duplicateAlert: true,
                duplicateCodigo: fichaAtualizada.codigo_ficha,
                // O payload do realtime traz a linha inteira, então o que o OCR
                // leu no papel vem de graça, sem consulta extra.
                duplicatePapel: papelDaRefoto(fichaAtualizada),
              },
              replace: true,
            });
            return;
          }

          setFicha(fichaAtualizada);

          setFormData(prev => ({
            ...prev,
            nome_cliente: fichaAtualizada.nome_cliente || prev.nome_cliente,
            telefone_cliente: fichaAtualizada.telefone_cliente ? formatarTelefoneInput(fichaAtualizada.telefone_cliente) : prev.telefone_cliente,
            codigo_ficha: fichaAtualizada.codigo_ficha || prev.codigo_ficha,
            tipo: fichaAtualizada.tipo || prev.tipo,
            status: fichaAtualizada.status || prev.status,
            data_retirada: fichaAtualizada.data_retirada ? parseDataSemFuso(fichaAtualizada.data_retirada) : prev.data_retirada,
            data_devolucao: fichaAtualizada.data_devolucao ? parseDataSemFuso(fichaAtualizada.data_devolucao) : prev.data_devolucao,
            data_festa: fichaAtualizada.data_festa ? parseDataSemFuso(fichaAtualizada.data_festa) : prev.data_festa,
            valor: fichaAtualizada.valor?.toString() || prev.valor,
            garantia: fichaAtualizada.garantia?.toString() || prev.garantia,
            paleto: fichaAtualizada.paleto || prev.paleto,
            calca: fichaAtualizada.calca || prev.calca,
            camisa: fichaAtualizada.camisa || prev.camisa,
            sapato: fichaAtualizada.sapato || prev.sapato,
            paleto_cor: fichaAtualizada.paleto_cor ?? prev.paleto_cor,
            paleto_lanificio: fichaAtualizada.paleto_lanificio ?? prev.paleto_lanificio,
            lanificio_id: fichaAtualizada.lanificio_id ?? prev.lanificio_id,
            sob_medida: fichaAtualizada.sob_medida ?? prev.sob_medida,
            paleto_categoria: fichaAtualizada.paleto_categoria ?? prev.paleto_categoria,
            camisa_fios: fichaAtualizada.camisa_fios ?? prev.camisa_fios,
            camisa_cor: fichaAtualizada.camisa_cor ?? prev.camisa_cor,
            sapato_tipo: fichaAtualizada.sapato_tipo ?? prev.sapato_tipo,
            pago: fichaAtualizada.pago ?? prev.pago,
          }));

          // O OCR preencheu campos: abre as seções que ganharam dado, sem fechar
          // o que o usuário já tinha aberto (merge aditivo).
          setOpenSections(prev => {
            const novas = seccoesComDados(fichaAtualizada);
            const merged = [...prev];
            for (const s of novas) if (!merged.includes(s)) merged.push(s);
            return merged;
          });

          if (fichaAtualizada.status === 'erro' && fichaAtualizada.erro_etapa !== 'ficha_duplicada') {
            setIsProcessing(false);
            toast({
              title: "Erro ao processar",
              description: "Não foi possível processar a imagem. Você pode preencher manualmente.",
              variant: "destructive"
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, isNewFicha, navigate]);

  // Label de item avulso a partir do registro (fallback: capitalize do slug).
  const labelItemAvulso = (slug: string) =>
    tiposItem.find(t => t.slug === slug)?.nome ?? (slug.charAt(0).toUpperCase() + slug.slice(1));

  // Lanifícios do tipo atual do toggle "Sob medida".
  const tipoLanificio = formData.sob_medida ? 'sob_medida' : 'padrao';
  const lanificiosDoTipo = lanificios.filter(l => l.tipo === tipoLanificio);
  // Lanifício legado gravado só como texto (sem lanificio_id) — exibido como fallback.
  const lanificioLegado = !formData.lanificio_id && formData.paleto_lanificio
    ? formData.paleto_lanificio
    : null;

  // Opções (cores/fios/tipos) vindas dos registros; valor legado fora da lista
  // aparece como opção extra selecionada para não sumir dado antigo.
  const opcoesComLegado = (lista: string[] | undefined, atual: string | null): string[] => {
    const base = lista ?? [];
    return atual && !base.includes(atual) ? [...base, atual] : base;
  };
  const coresPaleto = opcoesComLegado(opcoes?.paleto_cor, formData.paleto_cor);
  const fiosCamisa = opcoesComLegado(opcoes?.camisa_fios, formData.camisa_fios);
  const coresCamisa = opcoesComLegado(opcoes?.camisa_cor, formData.camisa_cor);
  const tiposSapato = opcoesComLegado(opcoes?.sapato_tipo, formData.sapato_tipo);

  // Ao mudar o tipo de atendimento: sair de venda limpa lanifício/sob_medida;
  // sair de aluguel limpa a categoria.
  const handleTipoChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      tipo: value,
      ...(prev.tipo === 'venda' && value !== 'venda'
        ? { sob_medida: false, lanificio_id: null }
        : {}),
      ...(prev.tipo === 'aluguel' && value !== 'aluguel'
        ? { paleto_categoria: null }
        : {}),
    }));
  };

  // Obrigatório novo pendente: lanifício (venda) ou categoria (aluguel), apenas
  // quando a ficha tem paletó e/ou calça (seção minimizada não obriga).
  const temPaletoOuCalca = !!formData.paleto || !!formData.calca;
  const pendencia: 'lanificio' | 'categoria' | null =
    formData.tipo === 'venda' && temPaletoOuCalca && !formData.lanificio_id ? 'lanificio' :
    formData.tipo === 'aluguel' && temPaletoOuCalca && !formData.paleto_categoria ? 'categoria' :
    null;

  // Nome do lanifício selecionado (para o dual-write em paleto_lanificio).
  const nomeLanificioSelecionado = formData.lanificio_id
    ? (lanificios.find(l => l.id === formData.lanificio_id)?.nome ?? formData.paleto_lanificio ?? null)
    : null;

  const abrirBlocoLanificio = () => {
    setOpenSections(prev => prev.includes('paleto-calca') ? prev : [...prev, 'paleto-calca']);
    setTimeout(() => {
      blocoLanificioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  const handleToggleTag = (tag: { id: string; nome: string }) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.some(t => t.id === tag.id)
        ? prev.tags.filter(t => t.id !== tag.id)
        : [...prev.tags, { id: tag.id, nome: tag.nome }],
    }));
  };

  const handleRemoverTag = (tagId: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t.id !== tagId),
    }));
  };

  // Tags selecionadas que não fazem parte das principais (exibidas como chips removíveis)
  const tagsExtras = formData.tags.filter(
    t => !tagsPadrao.some(p => p.id === t.id)
  );

  // Busca de tags existentes (a criação livre acabou): filtra as ativas não-padrão
  // ainda não selecionadas. Lista aparece com 2+ caracteres, no máximo 8 resultados.
  // Sem case e sem acento nos dois lados: "agro" encontra "Agrônomo".
  const resultadosBuscaTag = useMemo(() => {
    const q = normalizarBusca(buscaTag.trim());
    if (q.length < 2) return [];
    return tagsAtivas
      .filter(t => !t.padrao)
      .filter(t => !formData.tags.some(s => s.id === t.id))
      .filter(t => normalizarBusca(t.nome).includes(q))
      .slice(0, 8);
  }, [buscaTag, tagsAtivas, formData.tags]);

  const handleAdicionarProva = () => travarSubmit(async () => {
    try {
      await adicionarProva.mutateAsync();
      toast({ title: "Prova registrada", description: "Adicionada com sucesso." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes('Prova duplicada')) {
        toast({
          title: "Prova já registrada",
          description: "Já existe uma prova sua para este cliente nos últimos 10 minutos.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erro ao adicionar prova",
          description: msg || "Tente novamente.",
          variant: "destructive",
        });
      }
    }
  });

  const handleDeletarProva = async (provaId: string) => {
    try {
      await deletarProva.mutateAsync(provaId);
      toast({ title: "Prova removida" });
    } catch (err) {
      toast({
        title: "Erro ao remover prova",
        description: err instanceof Error ? err.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  // Marca a ficha de OUTRO vendedor como paga via RPC (marcar_ficha_paga).
  // Usado só no modo somente-leitura por dono: a RLS bloqueia o UPDATE direto, e a
  // RPC altera exclusivamente o campo `pago` (mão-única, escopo por unidade).
  const handleMarcarPago = async () => {
    if (!id) return;
    try {
      const { error } = await supabase.rpc('marcar_ficha_paga', { p_ficha_id: id });
      if (error) throw error;
      setFormData((prev) => ({ ...prev, pago: true }));
      setFicha((prev: any) => (prev ? { ...prev, pago: true } : prev));
      queryClient.invalidateQueries({ queryKey: ['fichas-processadas'] });
      toast({ title: "Pagamento registrado" });
    } catch (err) {
      toast({
        title: "Erro ao marcar como pago",
        description: err instanceof Error ? err.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (!formData.codigo_ficha) {
        toast({
          title: "Erro",
          description: "Código da ficha é obrigatório",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Obrigatórios novos (lanifício na venda / categoria no aluguel) — defesa em
      // profundidade além do botão dinâmico. Só vale quando há paletó e/ou calça.
      // Não se aplica a fichas já lançadas (núcleo travado, não há como corrigir aqui).
      if (pendencia && !nucleoBloqueado) {
        toast({
          title: pendencia === 'lanificio' ? "Selecione o lanifício" : "Selecione a categoria",
          description: pendencia === 'lanificio'
            ? "Escolha um lanifício para a venda com paletó e/ou calça."
            : "Escolha a categoria (Básico/Intermediário/Premium) para o aluguel com paletó e/ou calça.",
          variant: "destructive",
        });
        setLoading(false);
        abrirBlocoLanificio();
        return;
      }

      // Impede salvar com um código já usado por outra ficha.
      // IMPORTANTE: nunca deletamos a ficha atual aqui. O comportamento antigo
      // (deletar a ficha em edição e redirecionar para a outra) apagava a ficha
      // com os dados bons e, quando uma duplicata apontava para esta via
      // ficha_original_id, batia na FK NO ACTION e quebrava tanto o salvar
      // quanto o excluir. Agora apenas bloqueamos e avisamos.
      // Código da ficha é único POR UNIDADE (não global): restringe a checagem
      // à mesma unidade da ficha e só a fichas ativas/pendentes.
      const { data: duplicadas, error: dupErr } = await supabase
        .from('fichas')
        .select('id, status')
        .eq('codigo_ficha', formData.codigo_ficha)
        .eq('unidade_id', ficha?.unidade_id)
        .neq('id', id)
        .in('status', ['ativa', 'pendente'])
        .limit(1);

      if (dupErr) throw dupErr;

      if (duplicadas && duplicadas.length > 0) {
        toast({
          title: "Código de ficha já existe",
          description: `Já existe outra ficha com o código ${formData.codigo_ficha} nesta unidade. Altere o código ou abra a ficha existente para continuar.`,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      let clienteId: string | null = null;
      let telefoneNormalizado: string | null = null;

      if (formData.telefone_cliente && formData.telefone_cliente.trim() !== '') {
        telefoneNormalizado = normalizarTelefone(formData.telefone_cliente);

        if (!telefoneNormalizado) {
          toast({
            title: "Telefone inválido",
            description: "Não foi possível normalizar o telefone. Confira o formato e tente novamente.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        const authUser = (await supabase.auth.getUser()).data.user;

        const { data, error: criarError } = await supabase.functions.invoke('criar-cliente', {
          body: {
            nome: formData.nome_cliente || 'Cliente sem nome',
            telefone: telefoneNormalizado,
            // Cliente pertence ao vendedor da ficha (não a quem lançou —
            // relevante quando um administrativo lança para outro vendedor)
            vendedor_id: fichaVendedorId ?? authUser?.id,
            unidade_id: profile?.unidade_id ?? null,
            // Se a ficha já está vinculada a um cliente, atualiza ESSE cliente
            // (update por id) em vez de re-resolver por telefone — evita duplicar
            // ao corrigir o telefone de uma ficha já vinculada.
            cliente_id: ficha?.cliente_id ?? undefined,
          },
        });

        if (criarError || !data?.cliente_id) {
          // Em respostas não-2xx o supabase-js deixa data = null e criarError
          // vira um FunctionsHttpError genérico ("non-2xx status code"). A razão
          // real (ex.: "Telefone inválido...") vem no corpo da resposta, então
          // lemos criarError.context (o Response) para exibir a mensagem certa.
          let mensagem = data?.error || criarError?.message || 'Falha ao vincular cliente';
          const ctx = (criarError as { context?: Response } | null)?.context;
          if (ctx && typeof ctx.json === 'function') {
            try {
              const corpo = await ctx.json();
              if (corpo?.error) mensagem = corpo.error;
            } catch {
              // corpo não-JSON: mantém a mensagem anterior
            }
          }
          throw new Error(mensagem);
        }

        clienteId = data.cliente_id;
      }

      const novoStatus = ['pendente', 'erro'].includes(formData.status) ? 'ativa' : formData.status;

      const updateData: any = {
        nome_cliente: formData.nome_cliente || null,
        // Telefone congelado após o 1º save: preserva o valor persistido para não
        // disparar o trigger de bloqueio (garantia do banco continua valendo).
        telefone_cliente: telefoneBloqueado
          ? (ficha?.telefone_cliente ?? null)
          : (telefoneNormalizado || (formData.telefone_cliente?.trim() || null)),
        codigo_ficha: formData.codigo_ficha || null,
        tipo: formData.tipo || null,
        data_retirada: formatarDataParaBanco(formData.data_retirada),
        data_devolucao: formatarDataParaBanco(formData.data_devolucao),
        data_festa: formatarDataParaBanco(formData.data_festa),
        valor: formData.valor ? parseFloat(formData.valor.toString()) : null,
        garantia: formData.garantia ? parseFloat(formData.garantia.toString()) : null,
        paleto: formData.paleto || null,
        calca: formData.calca || null,
        camisa: formData.camisa || null,
        sapato: formData.sapato || null,
        paleto_cor: formData.paleto_cor || null,
        // Dual-write da transição: grava lanificio_id/sob_medida/paleto_categoria
        // E o texto paleto_lanificio (nome do lanifício ou null) para relatórios legados.
        lanificio_id: formData.lanificio_id || null,
        sob_medida: formData.sob_medida,
        paleto_categoria: formData.paleto_categoria || null,
        paleto_lanificio: nomeLanificioSelecionado,
        camisa_fios: formData.camisa_fios || null,
        camisa_cor: formData.camisa_cor || null,
        sapato_tipo: formData.sapato_tipo || null,
        // Pago é mão-única: uma vez pago, não volta a "não pago" por esta tela.
        pago: ficha?.pago ? true : formData.pago,
        // `clienteId` só é resolvido quando há telefone no formulário. Sem esta
        // guarda, salvar uma ficha sem telefone DESVINCULARIA o cliente que ela
        // já tinha — e ficha lançada pela foto de uma atividade do funil nasce
        // vinculada ao cliente do card antes de qualquer telefone existir. O
        // gatilho de ganho desiste quando cliente_id é null, então o card
        // ficaria aberto para sempre. Desvincular nunca foi a intenção de quem
        // apagou o telefone; manter o vínculo é o comportamento seguro.
        cliente_id: clienteId ?? ficha?.cliente_id ?? null,
        status: novoStatus,
        updated_at: new Date().toISOString(),
      };

      if (formData.status === 'erro' && novoStatus === 'ativa') {
        updateData.erro_etapa = null;
        updateData.ficha_original_id = null;
      }

      // Venda/comissão continuam do vendedor original: o vendedor responsável só
      // pode ser (re)definido por admin ENQUANTO a ficha não foi lançada.
      if (isAdmin && fichaVendedorId && !jaLancada) {
        updateData.vendedor_id = fichaVendedorId;
      }

      const { error } = await supabase
        .from("fichas")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      // Notificação da ficha no WhatsApp pausada em 11/09/2026: a chamada à edge
      // `notificar-ficha-whatsapp` saiu daqui. A edge continua deployada. Para
      // religar e o porquê, ver pamplona-crm/docs/PLANO-PAUSAR-WHATSAPP.md.

      // Sincroniza tags do cliente por id — só relações; tag nova não nasce
      // mais aqui (a seleção vem de tags existentes e a RLS de `tags` bloqueia
      // escrita fora da gestão). Cada vínculo grava autor/ficha/unidade para
      // os indicadores de tagueamento.
      if (clienteId) {
        const idsDesejados = Array.from(new Set(formData.tags.map(t => t.id)));

        const { data: relacoesAtuais } = await supabase
          .from('relacao_cliente_tag')
          .select('id, id_tag')
          .eq('id_cliente', clienteId);

        const idsAtuais = new Set((relacoesAtuais ?? []).map(r => r.id_tag));
        const idsDesejadosSet = new Set(idsDesejados);

        const inserir = idsDesejados.filter(id => !idsAtuais.has(id));
        if (inserir.length > 0) {
          const { error: tagsErro } = await supabase
            .from('relacao_cliente_tag')
            .insert(inserir.map(id_tag => ({
              id_cliente: clienteId,
              id_tag,
              created_by: user?.id ?? null,
              ficha_id: id ?? null,
              // Loja de origem: a da ficha; sem ela, a unidade alocada da
              // sessão (3 = "Todas" é virtual, não é loja).
              unidade_id: ficha?.unidade_id
                ?? (profile?.unidade_id && profile.unidade_id !== 3 ? profile.unidade_id : null),
            })));
          if (tagsErro) {
            toast({
              title: "Não foi possível salvar as tags",
              description: "A ficha foi salva, mas as tags do cliente não. Tente de novo.",
              variant: "destructive",
            });
          }
        }

        const removerIds = (relacoesAtuais ?? [])
          .filter(r => r.id_tag && !idsDesejadosSet.has(r.id_tag))
          .map(r => r.id);
        if (removerIds.length > 0) {
          const { error: removerErro } = await supabase
            .from('relacao_cliente_tag').delete().in('id', removerIds);
          if (removerErro) {
            toast({
              title: "Não foi possível remover tags",
              description: "A ficha foi salva, mas a remoção de tags falhou. Tente de novo.",
              variant: "destructive",
            });
          }
        }
      } else if (formData.tags.length > 0) {
        // Ficha sem cliente: as tags não têm onde ser gravadas — avisa em vez
        // de descartar em silêncio (auditoria de 04/08).
        toast({
          title: "Tags não salvas",
          description: "A ficha não tem cliente vinculado. Informe o telefone do cliente para salvar as tags.",
          variant: "destructive",
        });
      }

      queryClient.invalidateQueries({ queryKey: ['fichas-processadas'] });

      if (cliente_id) {
        navigate(`/cliente/${cliente_id}`);
      } else {
        navigate("/fichas");
      }
    } catch (error) {
      console.error("Erro ao atualizar ficha:", error);
      const rawMessage = error instanceof Error ? error.message : "";
      const telefoneBloqueadoErro = rawMessage.includes("Telefone da ficha");
      toast({
        title: telefoneBloqueadoErro ? "Telefone bloqueado" : "Erro ao salvar ficha",
        description: telefoneBloqueadoErro
          ? "O telefone não pode ser alterado após o primeiro salvamento da ficha."
          : (rawMessage || "Não foi possível salvar a ficha. Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(null);
  };

  const handleImageError = () => {
    setImageError('Não foi possível carregar a imagem. Verifique se a URL está correta.');
    setImageLoading(false);
  };

  const handleOpenImageModal = async () => {
    if (!ficha?.url_bucket) return;
    setImageLoading(true);
    setImageError(null);
    setSignedImageUrl(null);
    setShowImageModal(true);

    const { data, error } = await supabase.storage
      .from('fichas')
      .createSignedUrl(ficha.url_bucket, 60);

    if (error || !data?.signedUrl) {
      setImageError('Não foi possível gerar o link da imagem.');
      setImageLoading(false);
      return;
    }

    setSignedImageUrl(data.signedUrl);
  };

  if (isLoadingFicha) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header title="Carregando..." />
        <main className="flex-1 p-4 pb-20">
          <div className="max-w-4xl mx-auto space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Editar Ficha" />

      <main className="flex-1 p-4 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (cliente_id) {
                  navigate(`/cliente/${cliente_id}`);
                } else {
                  navigate("/fichas");
                }
              }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold">Editar Ficha</h1>
          </div>

          {showDuplicateBanner && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">Esta ficha já existe no sistema</p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  {/* A ficha inativada perde o código de propósito, então o
                      state pode chegar sem ele. Quem tem o código é esta ficha
                      aqui, a original, que é justamente a que já estava
                      cadastrada. */}
                  {(duplicateCodigo ?? ficha?.codigo_ficha)
                    ? `A ficha #${duplicateCodigo ?? ficha?.codigo_ficha} já estava cadastrada. `
                    : ''}
                  Você foi redirecionado para a ficha original.
                </p>

                {/* O que mudou entre o papel que ele acabou de fotografar e a
                    ficha gravada. Ele não pode corrigir aqui (ficha lançada é da
                    gestão), então isto existe para ele sair sabendo o que
                    reportar, em vez de sair sem saber que havia diferença. */}
                {diferencasDaRefoto(duplicatePapel, ficha).length > 0 && (
                  <div className="mt-2 rounded border border-yellow-300 dark:border-yellow-700 bg-yellow-100/60 dark:bg-yellow-900/40 p-2">
                    <p className="text-xs font-medium text-yellow-900 dark:text-yellow-100">
                      A foto que você tirou está diferente desta ficha:
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {diferencasDaRefoto(duplicatePapel, ficha).map(d => (
                        <li key={d.rotulo} className="text-xs text-yellow-800 dark:text-yellow-200 tabular-nums">
                          <span className="font-medium">{d.rotulo}:</span> no papel{' '}
                          <span className="font-semibold">{d.noPapel}</span>, nesta ficha{' '}
                          <span className="font-semibold">{d.naFicha}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1.5 text-xs text-yellow-700 dark:text-yellow-300">
                      Ficha já lançada só a gestão altera. Avise a gestão com o número
                      da ficha para corrigir.
                    </p>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowDuplicateBanner(false)}
                className="text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-100 flex-shrink-0"
                aria-label="Fechar aviso"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {isProcessing && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Processando dados da ficha...</p>
                <p className="text-xs text-blue-700 dark:text-blue-300">Os campos serão preenchidos automaticamente.</p>
              </div>
            </div>
          )}

          {wasProcessed && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900 dark:text-green-100">Dados da ficha processados com sucesso</p>
              </div>
            </div>
          )}

          {soLeituraDono && (
            <div className="mb-4 p-3 bg-muted border border-border rounded-lg flex items-start gap-3">
              <User className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Ficha de outro vendedor</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Você pode visualizar esta ficha, mas não editar os dados dela. Aqui você ainda
                  pode registrar prova, lançar pedido avulso (marcados em seu nome) e marcar como pago.
                </p>
              </div>
            </div>
          )}

          {nucleoBloqueado && !soLeituraDono && (
            <div className="mb-4 p-3 bg-muted border border-border rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Ficha já lançada</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Os dados de cadastro, peças e valores estão travados. Aqui você ainda pode
                  registrar prova, lançar pedido avulso, marcar como pago e adicionar tags.
                  Para alterar nome ou telefone, edite o cadastro do cliente.
                </p>
              </div>
            </div>
          )}

          {ficha?.status === 'erro' && isNewFicha && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-medium text-red-900 dark:text-red-100">Erro ao processar imagem</p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">Você pode preencher os campos manualmente ou reenviar a imagem.</p>
            </div>
          )}

          {ficha?.erro_etapa === 'upload' && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">A imagem desta ficha não foi salva</p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Ocorreu um erro ao salvar a foto no processamento. Esta ficha <strong>não será enviada ao WhatsApp</strong> e a imagem não ficará disponível. Refaça a foto para corrigir.
                </p>
              </div>
            </div>
          )}

          <div className="mb-4 flex gap-2">
            {ficha?.url_bucket && (
              <Button
                type="button"
                onClick={handleOpenImageModal}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <ImageIcon className="h-4 w-4" />
                Ver Ficha Original
              </Button>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={() => ficha?.cliente_id && navigate(`/cliente/${ficha.cliente_id}`)}
                    disabled={!ficha?.cliente_id}
                    className={`${ficha?.url_bucket ? 'flex-1' : 'w-full'} flex items-center justify-center gap-2`}
                  >
                    <User className="h-4 w-4" />
                    {jaLancada ? 'Editar Cadastro do Cliente' : 'Ver Cliente'}
                  </Button>
                </TooltipTrigger>
                {!ficha?.cliente_id && (
                  <TooltipContent>
                    <p>Cliente será vinculado ao salvar a ficha</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="space-y-6 [&_.border-input]:border-foreground/30">

            {/* Núcleo da ficha (cadastro/peças/valores) — travado após o lançamento
                OU quando é ficha de outro vendedor (nucleoReadOnly).
                fieldset[disabled] desabilita todos os controles internos de uma vez;
                `contents` preserva o layout (não gera caixa própria). */}
            <fieldset disabled={nucleoReadOnly} className="contents">

            {/* Cabeçalho */}
            <div className="space-y-4">
              <h3 className="text-base font-semibold">Cabeçalho</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome_cliente">Nome</Label>
                    <Input
                      id="nome_cliente"
                      name="nome_cliente"
                      value={formData.nome_cliente}
                      onChange={(e) => setFormData({ ...formData, nome_cliente: e.target.value })}
                      placeholder="Nome completo"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telefone_cliente">Telefone</Label>
                    <Input
                      id="telefone_cliente"
                      name="telefone_cliente"
                      value={formData.telefone_cliente}
                      onChange={(e) => setFormData({ ...formData, telefone_cliente: e.target.value })}
                      placeholder="(00) 00000-0000"
                      disabled={telefoneBloqueado}
                      title={telefoneBloqueado ? "O telefone não pode ser alterado após o primeiro salvamento da ficha." : undefined}
                    />
                    {telefoneBloqueado && (
                      <p className="text-xs text-muted-foreground">
                        O telefone não pode ser alterado após o primeiro salvamento.
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="codigo_ficha">Código da Ficha</Label>
                    <Input
                      id="codigo_ficha"
                      name="codigo_ficha"
                      value={formData.codigo_ficha}
                      onChange={(e) => setFormData({ ...formData, codigo_ficha: e.target.value })}
                      placeholder="Código"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tipo">Tipo de Atendimento</Label>
                    <Select value={formData.tipo} onValueChange={handleTipoChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aluguel">Aluguel</SelectItem>
                        <SelectItem value="venda">Venda</SelectItem>
                        <SelectItem value="ajuste">Ajuste</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Input
                      id="status"
                      name="status"
                      value={formData.status === "pendente" ? "Pendente" : formData.status === "ativa" ? "Ativa" : formData.status === "erro" ? "Erro" : formData.status}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Datas */}
            <div className="space-y-4">
              <h3 className="text-base font-semibold">Datas</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Data de Retirada</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.data_retirada && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_retirada ? format(formData.data_retirada, "PPP", { locale: ptBR }) : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_retirada}
                        onSelect={(date) => setFormData({ ...formData, data_retirada: date })}
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Data de Devolução</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.data_devolucao && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_devolucao ? format(formData.data_devolucao, "PPP", { locale: ptBR }) : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_devolucao}
                        onSelect={(date) => setFormData({ ...formData, data_devolucao: date })}
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Data da Festa</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.data_festa && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_festa ? format(formData.data_festa, "PPP", { locale: ptBR }) : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_festa}
                        onSelect={(date) => setFormData({ ...formData, data_festa: date })}
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <Separator />

            {/* Detalhes do Item */}
            <div className="space-y-4">
              <h3 className="text-base font-semibold">Detalhes do Item</h3>

              <Accordion
                type="multiple"
                value={openSections}
                onValueChange={setOpenSections}
                className="space-y-2"
              >
                {/* Paletó / Calça */}
                <AccordionItem value="paleto-calca" className="border rounded-md px-3">
                  <AccordionTrigger className="hover:no-underline py-3 text-sm font-medium">
                    Paletó / Calça
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="paleto">Paletó</Label>
                        <Input
                          id="paleto"
                          value={formData.paleto}
                          onChange={(e) => setFormData({ ...formData, paleto: e.target.value })}
                          placeholder="Número"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="calca">Calça</Label>
                        <Input
                          id="calca"
                          value={formData.calca}
                          onChange={(e) => setFormData({ ...formData, calca: e.target.value })}
                          placeholder="Número"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Cor</Label>
                      <div className="flex flex-wrap gap-2">
                        {coresPaleto.map(cor => (
                          <Button
                            key={cor}
                            type="button"
                            size="sm"
                            variant={formData.paleto_cor === cor ? 'default' : 'outline'}
                            onClick={() => setFormData({ ...formData, paleto_cor: formData.paleto_cor === cor ? null : cor })}
                          >
                            {cor}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Lanifício (venda) / Categoria (aluguel) — varia por tipo de atendimento */}
                    <div ref={blocoLanificioRef}>
                      {formData.tipo === 'aluguel' && (
                        <div className="space-y-2">
                          <Label>
                            Categoria
                            {temPaletoOuCalca && <span className="text-destructive"> *</span>}
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {CATEGORIAS_ALUGUEL.map(cat => (
                              <Button
                                key={cat}
                                type="button"
                                size="sm"
                                variant={formData.paleto_categoria === cat ? 'default' : 'outline'}
                                onClick={() => setFormData({ ...formData, paleto_categoria: formData.paleto_categoria === cat ? null : cat })}
                              >
                                {cat}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}

                      {formData.tipo === 'venda' && (
                        <div className="space-y-2">
                          <Label>
                            Lanifício
                            {temPaletoOuCalca && <span className="text-destructive"> *</span>}
                          </Label>
                          <div className="flex flex-wrap items-center gap-2">
                            <Select
                              value={formData.lanificio_id ?? ''}
                              onValueChange={(value) => setFormData({ ...formData, lanificio_id: value || null })}
                            >
                              <SelectTrigger className="w-full sm:w-64">
                                <SelectValue placeholder="Selecione o lanifício" />
                              </SelectTrigger>
                              <SelectContent>
                                {lanificiosDoTipo.map(l => (
                                  <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              size="sm"
                              variant={formData.sob_medida ? 'default' : 'outline'}
                              onClick={() => setFormData({ ...formData, sob_medida: !formData.sob_medida, lanificio_id: null })}
                            >
                              Sob medida
                            </Button>
                          </div>
                          {lanificioLegado && (
                            <p className="text-xs text-muted-foreground">
                              Lanifício registrado: {lanificioLegado} — selecione no dropdown para atualizar.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Camisa */}
                <AccordionItem value="camisa" className="border rounded-md px-3">
                  <AccordionTrigger className="hover:no-underline py-3 text-sm font-medium">
                    Camisa
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="camisa">Número</Label>
                      <Input
                        id="camisa"
                        value={formData.camisa}
                        onChange={(e) => setFormData({ ...formData, camisa: e.target.value })}
                        placeholder="Número"
                        className="max-w-[160px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fios</Label>
                      <div className="flex flex-wrap gap-2">
                        {fiosCamisa.map(fio => (
                          <Button
                            key={fio}
                            type="button"
                            size="sm"
                            variant={formData.camisa_fios === fio ? 'default' : 'outline'}
                            onClick={() => setFormData({ ...formData, camisa_fios: formData.camisa_fios === fio ? null : fio })}
                          >
                            {fio}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Cor</Label>
                      <div className="flex flex-wrap gap-2">
                        {coresCamisa.map(cor => (
                          <Button
                            key={cor}
                            type="button"
                            size="sm"
                            variant={formData.camisa_cor === cor ? 'default' : 'outline'}
                            onClick={() => setFormData({ ...formData, camisa_cor: formData.camisa_cor === cor ? null : cor })}
                          >
                            {cor}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Sapato */}
                <AccordionItem value="sapato" className="border rounded-md px-3">
                  <AccordionTrigger className="hover:no-underline py-3 text-sm font-medium">
                    Sapato
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sapato">Número</Label>
                      <Input
                        id="sapato"
                        value={formData.sapato}
                        onChange={(e) => setFormData({ ...formData, sapato: e.target.value })}
                        placeholder="Número"
                        className="max-w-[160px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <div className="flex flex-wrap gap-2">
                        {tiposSapato.map(tipo => (
                          <Button
                            key={tipo}
                            type="button"
                            size="sm"
                            variant={formData.sapato_tipo === tipo ? 'default' : 'outline'}
                            onClick={() => setFormData({ ...formData, sapato_tipo: formData.sapato_tipo === tipo ? null : tipo })}
                          >
                            {tipo}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            </fieldset>

            <Separator />

            {/* Provas */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Provas {provas.length > 0 && <span className="text-muted-foreground text-sm font-normal">({provas.length})</span>}
                </h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAdicionarProva}
                  disabled={adicionarProva.isPending || !id}
                >
                  {adicionarProva.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                  Adicionar Prova
                </Button>
              </div>
              {provas.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhuma prova registrada.</p>
              ) : (
                <div className="space-y-1">
                  {provas.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm p-2 rounded border border-border">
                      <span>
                        {format(new Date(p.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </span>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeletarProva(p.id)}
                        disabled={deletarProva.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Pedidos Avulsos */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4" />
                  Pedidos Avulsos
                </h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!id}
                  onClick={() => { setPedidoEditando(undefined); setPedidoModalOpen(true); }}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Adicionar Pedido
                </Button>
              </div>

              {pedidos.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum pedido avulso cadastrado.</p>
              ) : (
                <Accordion type="multiple" className="space-y-2">
                  {pedidos.map((pedido) => (
                    <AccordionItem
                      key={pedido.id}
                      value={pedido.id}
                      className="border rounded-md px-3"
                    >
                      <AccordionTrigger className="hover:no-underline py-3">
                        <div className="flex items-center gap-3 flex-1 text-left">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{pedido.vendedor_nome}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(pedido.created_at), "dd/MM/yyyy", { locale: ptBR })}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-sm font-medium tabular-nums">
                              {pedido.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                            <Badge variant={pedido.pago ? 'default' : 'secondary'} className="text-xs">
                              {pedido.pago ? 'Pago' : 'Pendente'}
                            </Badge>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-3">
                        <div className="space-y-2">
                          {pedido.itens.filter((i) => i.quantidade > 0).map((item) => (
                            <div key={item.tipo_item} className="flex justify-between text-sm">
                              <span>{labelItemAvulso(item.tipo_item)} × {item.quantidade}</span>
                              <span className="tabular-nums text-muted-foreground">
                                {(item.quantidade * (item.valor_unitario ?? 0))
                                  .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </span>
                            </div>
                          ))}
                          {pedido.garantia != null && pedido.garantia > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground pt-1">
                              <DollarSign className="h-3 w-3" />
                              Entrada: {pedido.garantia.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </div>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => { setPedidoEditando(pedido); setPedidoModalOpen(true); }}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Editar
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}

              <PedidoModal
                fichaId={id}
                pedido={pedidoEditando}
                open={pedidoModalOpen}
                onClose={() => setPedidoModalOpen(false)}
              />
            </div>

            {/* Pagamento */}
            <div className="space-y-4">
              <h3 className="text-base font-semibold">Pagamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valor">Valor (R$)</Label>
                  <Input
                    id="valor"
                    name="valor"
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    placeholder="0,00"
                    disabled={nucleoReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="garantia">Garantia (R$)</Label>
                  <Input
                    id="garantia"
                    name="garantia"
                    type="number"
                    step="0.01"
                    value={formData.garantia}
                    onChange={(e) => setFormData({ ...formData, garantia: e.target.value })}
                    placeholder="0,00"
                    disabled={nucleoReadOnly}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="pago"
                  checked={formData.pago}
                  // Pago é mão-única: pode ir de "não pago" → "pago", nunca o contrário.
                  // Já pago + (lançada ou ficha de outro vendedor) = travado.
                  disabled={(nucleoBloqueado || soLeituraDono) && formData.pago}
                  onCheckedChange={(checked) => {
                    if ((nucleoBloqueado || soLeituraDono) && !checked) return;
                    // Ficha de outro vendedor: só o campo `pago` pode mudar, e isso
                    // passa pela RPC marcar_ficha_paga (a RLS bloqueia UPDATE direto).
                    if (soLeituraDono) {
                      if (checked) handleMarcarPago();
                      return;
                    }
                    setFormData({ ...formData, pago: checked });
                  }}
                />
                <Label htmlFor="pago">Pagamento realizado</Label>
              </div>

              {isAdmin && !jaLancada && (
                <div className="space-y-2">
                  <Label>Vendedor responsável pela ficha</Label>
                  <Select value={fichaVendedorId ?? ''} onValueChange={setFichaVendedorId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o vendedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendedores.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Separator />

            {/* Tags */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold">Tags</h3>

              {/* Ficha de outro vendedor: as tags também ficam somente-leitura. */}
              <fieldset disabled={soLeituraDono} className="contents">

              {/* Tags principais (padrao = true) — botões on/off */}
              {tagsPadrao.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tagsPadrao.map((tag) => {
                    const selected = formData.tags.some(t => t.id === tag.id);
                    return (
                      <Button
                        key={tag.id}
                        type="button"
                        size="sm"
                        variant={selected ? 'default' : 'outline'}
                        onClick={() => handleToggleTag(tag)}
                      >
                        {tag.nome}
                      </Button>
                    );
                  })}
                </div>
              )}

              {/* Tags adicionais selecionadas */}
              {tagsExtras.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tagsExtras.map((tag) => (
                    <Badge key={tag.id} variant="secondary" className="gap-1 pr-1">
                      {tag.nome}
                      <button
                        type="button"
                        onClick={() => handleRemoverTag(tag.id)}
                        className="rounded-full hover:bg-foreground/10 p-0.5"
                        aria-label={`Remover tag ${tag.nome}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Buscar outra tag — só tags existentes; criar é papel da gestão (CRM) */}
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={buscaTag}
                    onChange={(e) => setBuscaTag(e.target.value)}
                    placeholder="Buscar outra tag…"
                    className="pl-9"
                  />
                </div>
                {buscaTag.trim().length >= 2 && (
                  <div className="rounded-md border border-border divide-y divide-border max-h-48 overflow-y-auto">
                    {resultadosBuscaTag.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        Nenhuma tag encontrada. Fale com seu gestor para adicionar novas tags.
                      </p>
                    ) : (
                      resultadosBuscaTag.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60"
                          onClick={() => {
                            handleToggleTag(tag);
                            setBuscaTag("");
                          }}
                        >
                          {tag.nome}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              </fieldset>
            </div>

            {/* Botões de ação */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => navigate("/fichas")}
                className="flex-1"
              >
                {soLeituraDono ? 'Voltar' : 'Cancelar'}
              </Button>
              {/* Ficha de outro vendedor: sem botão de salvar (núcleo é somente-leitura;
                  provas/pedidos/pago têm ações próprias). */}
              {!soLeituraDono && (
                pendencia && !nucleoBloqueado ? (
                  <Button
                    type="button"
                    onClick={abrirBlocoLanificio}
                    variant="secondary"
                    className="flex-1"
                  >
                    {pendencia === 'lanificio' ? 'Selecionar Lanifício' : 'Selecionar Categoria'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSave}
                    disabled={loading}
                    variant="success"
                    className="flex-1"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {rotuloBotaoFicha(jaLancada)}
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Modal de Imagem */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogTitle className="sr-only">Visualização da Ficha Original</DialogTitle>
          <DialogDescription className="sr-only">
            Imagem digitalizada da ficha de atendimento original
          </DialogDescription>
          <div className="relative w-full h-full flex items-center justify-center">
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            {imageError && (
              <div className="absolute inset-0 flex items-center justify-center bg-background">
                <p className="text-destructive">{imageError}</p>
              </div>
            )}
            {signedImageUrl && (
              <img
                src={signedImageUrl}
                alt="Ficha Original"
                className="max-w-full max-h-[80vh] object-contain"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
